<template>
  <div class="info-card">
    <div class="card-header">
      <div class="card-icon">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
          <circle cx="9" cy="7" r="4"/>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
          <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
        </svg>
      </div>
      <h3>Csoport információ</h3>
    </div>
    
    <div class="info-list" v-if="childInfo">
      <div class="info-item">
        <div class="info-label">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
          </svg>
          Csoportok
        </div>
        <div class="info-value">
          <div class="groups-display">
            <div v-if="!childGroups || childGroups.length === 0" class="no-groups">
              Nincs hozzárendelt csoport
            </div>
            <div v-else class="groups-list">
              <span
                v-for="group in childGroups"
                :key="group.id"
                :class="['group-badge', { 'group-badge--primary': group.is_primary }]"
              >
                {{ group.group_name }}
                <span v-if="group.is_primary" class="primary-indicator">(Elsődleges)</span>
                <button
                  v-if="!group.is_primary && (isAdmin || isTeacher || isAssistant)"
                  @click="$emit('set-primary-group', group.group_id)"
                  class="set-primary-btn"
                  title="Beállítás elsődlegesnek"
                >
                  ★
                </button>
                <button
                  v-if="isAdmin || isTeacher || isAssistant"
                  @click="$emit('remove-group', group.group_id)"
                  class="remove-group-btn"
                  title="Csoport eltávolítása"
                >
                  ×
                </button>
              </span>
            </div>
            <div v-if="isAdmin || isTeacher || isAssistant" class="group-actions">
              <button
                v-if="!isAddingGroup"
                @click="isAddingGroup = true"
                class="modern-btn modern-btn--primary"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="12" y1="5" x2="12" y2="19"/>
                  <line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                Csoport hozzáadása
              </button>
              <div v-if="isAddingGroup" class="add-group-form">
                <select
                  v-model="selectedGroupToAdd"
                  class="modern-select"
                  style="margin-right: 0.5rem;"
                >
                  <option value="">Válassz csoportot...</option>
                  <option
                    v-for="group in availableGroupsForUser"
                    :key="group.id"
                    :value="group.id"
                    :disabled="childGroups && childGroups.some(cg => cg.group_id === group.id)"
                  >
                    {{ group.name }}
                  </option>
                </select>
                <label class="checkbox-group" style="margin-right: 0.5rem;">
                  <input
                    type="checkbox"
                    v-model="isPrimaryForNewGroup"
                    class="modern-checkbox"
                  >
                  <span class="checkbox-custom"></span>
                  Elsődleges csoport
                </label>
                <button
                  @click="onAddGroup"
                  class="modern-btn modern-btn--success"
                  :disabled="!selectedGroupToAdd"
                >
                  Hozzáadás
                </button>
                <button
                  @click="cancelAddingGroup"
                  class="modern-btn modern-btn--secondary"
                >
                  Mégse
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="info-item" v-if="(isAdmin || isTeacher || isAssistant) && childInfo.childDesiredGrpId && childInfo.childDesiredGrpId.Valid">
        <div class="info-label">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M9 12l2 2 4-4"/>
          </svg>
          Kívánt csoport
        </div>
        <div class="info-value d-flex align-items-center gap-2 flex-wrap">
          <span class="group-badge group-badge--desired">{{ getGroupName(childInfo.childDesiredGrpId.Int64) }}</span>
          <button
            v-if="!isInGroup(childInfo.childDesiredGrpId.Int64)"
            @click="assignDesiredGroup"
            class="modern-btn modern-btn--success btn-sm"
            style="padding: 0.35rem 0.75rem; font-size: 0.8rem; display: inline-flex; align-items: center; gap: 0.35rem;"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
            Beosztás a kívánt csoportba
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ChildGroupsCard',
  props: {
    childInfo: {
      type: Object,
      default: null
    },
    childGroups: {
      type: Array,
      default: () => []
    },
    availableGroupsForUser: {
      type: Array,
      default: () => []
    },
    isAdmin: {
      type: Boolean,
      default: false
    },
    isTeacher: {
      type: Boolean,
      default: false
    },
    isAssistant: {
      type: Boolean,
      default: false
    }
  },
  emits: ['add-group', 'remove-group', 'set-primary-group'],
  data() {
    return {
      isAddingGroup: false,
      selectedGroupToAdd: '',
      isPrimaryForNewGroup: false
    };
  },
  methods: {
    onAddGroup() {
      if (!this.selectedGroupToAdd) return;
      this.$emit('add-group', {
        groupId: this.selectedGroupToAdd,
        isPrimary: this.isPrimaryForNewGroup
      });
      this.cancelAddingGroup();
    },
    cancelAddingGroup() {
      this.isAddingGroup = false;
      this.selectedGroupToAdd = '';
      this.isPrimaryForNewGroup = false;
    },
    getGroupName(groupId) {
      const g = this.availableGroupsForUser.find(grp => grp.id === groupId);
      return g ? g.name : 'Ismeretlen csoport';
    },
    isInGroup(groupId) {
      return this.childGroups && this.childGroups.some(cg => cg.group_id === groupId);
    },
    assignDesiredGroup() {
      if (!this.childInfo?.childDesiredGrpId?.Int64) return;
      this.$emit('add-group', {
        groupId: this.childInfo.childDesiredGrpId.Int64,
        isPrimary: true
      });
    }
  }
};
</script>

<style scoped>
.info-card {
  background: var(--card-bg);
  border-radius: 16px;
  box-shadow: var(--shadow);
  overflow: hidden;
  border: 1px solid var(--color-border);
}

.card-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem 2rem;
  background: var(--color-background-soft);
  border-bottom: 1px solid var(--color-border);
}

.card-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: var(--button-primary, #2563eb);
  border-radius: 12px;
  color: white;
}

.card-header h3 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--color-text);
}

.info-list {
  padding: 1.5rem 2rem;
}

.info-item {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem 0;
  border-bottom: 1px solid var(--color-border);
}

.info-item:last-child {
  border-bottom: none;
}

.info-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 140px;
  font-weight: 600;
  color: var(--color-text);
}

.info-label svg {
  color: var(--button-primary, #2563eb);
  flex-shrink: 0;
}

.info-value {
  flex: 1;
  color: var(--color-text-muted);
  word-break: break-word;
}

.groups-display {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.no-groups {
  color: var(--color-text-muted);
  font-style: italic;
  padding: 1rem;
  text-align: center;
  background: var(--color-background-soft);
  border-radius: 8px;
}

.groups-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.group-badge {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--success, #10b981);
  color: white;
  padding: 0.375rem 0.75rem;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 500;
}

.group-badge--primary {
  background: var(--primary-blue, #2563eb);
  border: 2px solid var(--primary-blue, #2563eb);
}

.group-badge--desired {
  background: var(--warning, #f59e0b);
  color: white;
}

.primary-indicator {
  font-size: 0.75rem;
  opacity: 0.9;
}

.set-primary-btn {
  background: none;
  border: none;
  color: #fbbf24;
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;
  padding: 0;
  margin-left: 0.25rem;
  transition: transform 0.2s ease;
}

.set-primary-btn:hover {
  transform: scale(1.2);
}

.remove-group-btn {
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  font-size: 1.2rem;
  line-height: 1;
  padding: 0;
  margin-left: 0.25rem;
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.remove-group-btn:hover {
  opacity: 1;
}

.group-actions {
  margin-top: 0.5rem;
}

.add-group-form {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-top: 0.5rem;
  padding: 1rem;
  background: var(--color-background-soft);
  border-radius: 8px;
  border: 1px solid var(--color-border);
}

.checkbox-group {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  cursor: pointer;
  user-select: none;
  font-size: 0.875rem;
  color: var(--color-text);
}
</style>
